import  { useEffect, useState } from "react";
import * as constants from "../constants";
import { ERRORICON, UPLOADICON } from "../IconConstants";
import parser from "html-react-parser";
import Axios from "axios";
import {
  FileText,
  Upload,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  FileUp,
  Send,
  X,
  Loader2,
  ChevronRight,
  Calendar,
  MessageSquare,
  File,
  Download,
  ClipboardList,
  AlertTriangle,
  FileSpreadsheet,
  Presentation
} from "lucide-react";
import { toast } from "react-toastify";

const getDocIconConfig = (fileName = "") => {
  const ext = fileName.split(".").pop()?.toLowerCase();

  if (ext === "pdf") return { Icon: FileText, className: "text-red-500" };
  if (ext === "doc" || ext === "docx") return { Icon: FileText, className: "text-blue-500" };
  if (ext === "xls" || ext === "xlsx" || ext === "csv")
    return { Icon: FileSpreadsheet, className: "text-green-500" };
  if (ext === "ppt" || ext === "pptx") return { Icon: Presentation, className: "text-orange-500" };

  return { Icon: File, className: "text-gray-500" };
};


// Loading Skeleton
const AssessmentsSkeleton = () => {
  return (
    <div className="space-y-4 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-gray-100 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
            <div className="flex-1">
              <div className="h-5 bg-gray-200 rounded w-2/3 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            </div>
            <div className="w-24 h-10 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status, statusCode }) => {
  const getStatusConfig = (code) => {
    switch (parseInt(code)) {
      case 1:
      case 2:
        return {
          bg: "bg-amber-50",
          text: "text-amber-700",
          border: "border-amber-200",
          icon: Clock,
          label: status || "Pending"
        };
      case 3:
      case 5:
        return {
          bg: "bg-green-50",
          text: "text-green-700",
          border: "border-green-200",
          icon: CheckCircle2,
          label: status || "Completed"
        };
      case 4:
      case 7:
        return {
          bg: "bg-red-50",
          text: "text-red-700",
          border: "border-red-200",
          icon: XCircle,
          label: status || "Resubmit Required"
        };
      case 6:
        return {
          bg: "bg-red-50",
          text: "text-red-800",
          border: "border-red-300",
          icon: AlertCircle,
          label: status || "Unsuccessful"
        };
      default:
        return {
          bg: "bg-gray-50",
          text: "text-gray-700",
          border: "border-gray-200",
          icon: Clock,
          label: status || "Awaiting"
        };
    }
  };

  const config = getStatusConfig(statusCode);
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
};

// Empty State Component
const EmptyState = () => {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <ClipboardList className="w-8 h-8 text-gray-400" />
      </div>
      <h4 className="text-base font-medium text-gray-900 mb-1">No Assessments</h4>
      <p className="text-sm text-gray-500 text-center max-w-sm">
        There are no assessments available for this subject yet.
      </p>
    </div>
  );
};

// File Item Component
const FileItem = ({ file, onRemove }) => {

  const { Icon, className } = getDocIconConfig(file.name);
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 group hover:bg-gray-100 transition-colors duration-200">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${className}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
          <p className="text-xs text-gray-500">
            {(file.size / 1024).toFixed(1)} KB
          </p>
        </div>
      </div>
      <button
        onClick={() => onRemove(file)}
        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// Assessment Card Component
const AssessmentCard = ({ 
  assessment, 
  onUpload, 
  onToggleDetails, 
  onIconClick,
  programId 
}) => {
  const [expanded, setExpanded] = useState(false);
  const [subData, setSubData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const handleToggle = async () => {
    if (!expanded && !subData) {
      setLoadingDetails(true);
      try {
        const res = await Axios.get(
          constants.BASEURL +
            "EnrolmentAssessment/Submissions/" +
            assessment.controlId +
            "?programId=" +
            programId
        );
        setSubData(res.data);
      } catch (error) {
        console.log("Error loading details:", error);
      } finally {
        setLoadingDetails(false);
      }
    }
    setExpanded(!expanded);
  };

  const hasDocuments = assessment?.assessmentDocuments?.listData?.length > 0;
  const hasSubmissions = assessment?.iconDocs?.[1]?.showIconDocs;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200">
      {/* Main Content */}
      <div className="p-3.5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="w-11 h-11 rounded-xl flex items-center bg-blue-50 justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2">
              {assessment.headingText?.split(' duedate:')[0]}
            </h4>
            
            {/* Description */}
            {assessment.itemList?.[0]?.itemText && (
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {assessment.itemList[0].itemText}
              </p>
            )}

            {/* Status & Info Row */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {assessment.itemList?.[1]?.itemText && (
                <div className="text-sm">
                  {assessment.itemList[1].itemText}
                </div>
              )}
            </div>

            {/* Submission Info */}
            {assessment.itemList?.[2]?.itemText && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <span>{assessment.itemList[2].itemText}</span>
              </div>
            )}

            {/* Notes if completed */}
            {assessment.itemList?.[3]?.itemText && (
              <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 text-green-600 mt-0.5" />
                  <p className="text-sm text-green-700">{assessment.itemList[3].itemText}</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Button */}
          {assessment.buttonFunction && !assessment.hideButton && (
            <button
              onClick={assessment.buttonFunction}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex-shrink-0
                ${assessment.buttonText === "Resubmit" 
                  ? "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200" 
                  : "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30"
                }`}
            >
              <Upload className="w-4 h-4" />
              {assessment.buttonText}
            </button>
          )}
        </div>

        {/* Quick Actions */}
        {(hasDocuments || hasSubmissions) && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            {hasDocuments && (
              <button
                onClick={handleToggle}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
              >
                <FileText className="w-4 h-4" />
                Assessment Documents
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              </button>
            )}
            {hasSubmissions && (
              <button
                onClick={handleToggle}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
              >
                <Send className="w-4 h-4" />
                View Submissions
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 p-5 animate-fadeIn">
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              <span className="ml-2 text-sm text-gray-500">Loading details...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Assessment Documents */}
              {assessment.assessmentDocuments?.listData?.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Assessment Documents
                  </h5>
                  <div className="space-y-2">
                    {assessment.assessmentDocuments.listData.map((doc) => (
                      <a
                        key={doc.itemId}
                        href={doc.linkTarget}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                            <File className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                            {doc.itemName}
                          </span>
                        </div>
                        <Download className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Submissions */}
              {subData && subData.length > 0 && (
                <div>
                  <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4 text-green-500" />
                    Submission History
                  </h5>
                  <div className="space-y-3">
                    {subData.map((submission, idx) => (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border ${
                          parseInt(submission.createUser) > 0
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            parseInt(submission.createUser) > 0
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {parseInt(submission.createUser) > 0 ? 'Review' : 'Submission'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {submission.submissionDate}
                          </span>
                        </div>
                        {(submission.submissionNotes || submission.assessmentNotes) && (
                          <p className="text-sm text-gray-600 mb-3">
                            {parseInt(submission.createUser) > 0 
                              ? submission.assessmentNotes 
                              : submission.submissionNotes}
                          </p>
                        )}
                        {submission.submissionDocuments?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {submission.submissionDocuments.map((doc) => {
                              const { Icon, className } = getDocIconConfig(doc.docName);
                              return (<a
                                key={doc.documentID}
                                href={doc.downloadLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-all duration-200"
                              >
                                <Icon className={`w-3.5 h-3.5 ${className}`} />
                                {doc.docName}
                              </a>)
                            }
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  loading, 
  tempFiles, 
  setTempFiles,
  fields,
  setFields,
  error,
  setFormError 
}) => {
  const MAX_FILES = 10;

const filesOnChange = (e) => {
  const selected = Array.from(e?.target?.files || []);
  if (!selected.length) return;

  setTempFiles((prev) => {
    const currentCount = prev.length;

    // Remove duplicates by name
    const uniqueNew = selected.filter(
      (f) => !prev.some((existing) => existing.name === f.name)
    );

    // If user selected only duplicates
    if (uniqueNew.length === 0) {
      setFormError?.("Those files are already selected.");
      e.target.value = "";
      return prev;
    }

    const nextCount = currentCount + uniqueNew.length;

    // If total would exceed limit
    if (nextCount > MAX_FILES) {
      setFormError?.(`You can select up to ${MAX_FILES} files.`);

      const remaining = Math.max(0, MAX_FILES - currentCount);
      e.target.value = "";
      return remaining > 0 ? [...prev, ...uniqueNew.slice(0, remaining)] : prev;
    }

    setFormError?.(null);
    e.target.value = "";
    return [...prev, ...uniqueNew];
  });
};

  const removeFile = (file) => {
    setTempFiles(tempFiles.filter((item) => item.name !== file.name));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Submit Assessment</h3>
                <p className="text-sm text-gray-500">Upload your completed work</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Warning Message */}
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Please review your assessments carefully before submission. Once uploaded, there is no option to delete or resubmit any documents found to be incorrect.
              </p>
            </div>

            {/* Notes Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Submission Notes
              </label>
              <textarea
                name="SubmissionNotes"
                value={fields?.SubmissionNotes || ""}
                onChange={(e) => setFields({ ...fields, SubmissionNotes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-400 resize-none text-sm outline-none"
                placeholder="Add any notes about your submission..."
              />
            </div>

            {/* Uploaded Files */}
            {tempFiles.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selected Files ({tempFiles.length})
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {tempFiles.map((file, idx) => (
                    <FileItem key={idx} file={file} onRemove={removeFile} />
                  ))}
                </div>
              </div>
            )}

            {/* Upload Area */}
            <div className="relative">
              <input
                type="file"
                multiple
                onChange={filesOnChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-200">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <FileUp className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  PDF, DOC, DOCX, XLS, XLSX up to 10MB each
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={loading || tempFiles.length === 0}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-sm sm:text-base font-medium whitespace-nowrap text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Assessment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Assessments = (props) => {
  const [state, setState] = useState({
    modalState: false,
    alertState: false,
    alertMessage: "",
    alertIcon: "",
    iconColor: "",
    fields: undefined,
    listItems: [],
  });

  const [tempFiles, setTempFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [formError, setFormError] = useState(null);
  const { objectId, objectType, programId, cardHeaderSettings } = props;
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    fetchData();
  }, [programId, refresh]);

  const closeModal = () => {
    setState((prevState) => ({
      ...prevState,
      modalState: false,
      fields: undefined,
    }));
    setTempFiles([]);
    setFormError(null);
  };

  const submitAssessment = async () => {
    if (!Array.isArray(tempFiles) || tempFiles?.length < 1) {
      setFormError("Please select at least one file to upload.");
      return;
    }

    let userConfirmation = window.confirm(
      "Please review your assessments carefully before submission. Once uploaded, there is no option to delete or resubmit any documents found to be incorrect."
    );

    if (!userConfirmation) return;

    setLoading(true);
    setFormError(null);

    const form = new FormData();
    tempFiles?.forEach((element) => {
      form.append("file", element);
    });

    for (const key in state?.fields) {
      if (state?.fields.hasOwnProperty(key)) {
        form.append(key, state?.fields[key]);
      }
    }

    try {
      const result = await Axios.post(
        constants.BASEURL + "EnrolmentAssessment/upload",
        form
      );

      const data = result.data;

      const isSuccess =  data?.success === true || (typeof data?.submissionId === "number" && data.submissionId > 0);

      if (!isSuccess) {
        setFormError(result.data.message);
        return;
      }

      setState((prevState) => ({
        ...prevState,
        alertState: true,
        alertMessage: "Assignment submitted successfully.",
        alertIcon: "SUCCESSICON",
        iconColor: "green",
        fields: undefined,
        modalState: false
      }));
      
      closeModal();
      toast.success("Assignment submitted successfully.")
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        alertState: true,
        alertMessage: "An error occurred while submitting. Please try again.",
        alertIcon: ERRORICON,
        iconColor: "red",
        fields: undefined,
      }));
      console.error(error);
    } finally {
      setTempFiles([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (state.alertState && state.iconColor === "green") {
      setRefresh(!refresh);
    }
  }, [state.alertState]);

  const fetchData = async () => {
    setPageLoading(true);
    try {
      const response = await Axios.get(
        constants.BASEURL + "EnrolmentAssessment/" + programId,
        {
          params: {
            objectId: objectId,
            objectType: objectType,
          },
        }
      );
      transposeAssessmentData([...response.data]);
    } catch (error) {
      console.error(error);
    } finally {
      setPageLoading(false);
    }
  };

  const transposeAssessmentDocuments = (docData) => {
    if (!docData || docData.length === 0) return null;

    return {
      key: docData[0].documentID,
      id: "assessment",
      listLabel: "Assessment Documents",
      listData: docData.map((docs) => ({
        itemId: docs.documentID,
        itemIcon: "",
        itemName: docs.docName,
        linkTarget: docs.downloadLink,
      })),
    };
  };

  const getAssessmentStatusColor = (status) => {
    switch (parseInt(status)) {
      case 1:
      case 2:
        return "#FB923C";
      case 3:
      case 5:
        return "#047857";
      case 4:
      case 7:
        return "#DC2626";
      case 6:
        return "#7F1D1D";
      default:
        return "inherit";
    }
  };

  const uploadDocument = (item) => {
    setState((prevState) => ({
      ...prevState,
      fields: {
        assessmentId: item?.assessmentID,
        submissionId: item?.lastSubmissionID,
        programId: programId,
        subjectId: item.objectID,
      },
      modalState: true,
    }));
  };

  const answerQuiz = (item) => {
    window.open(window.location.origin + "/AssessmentQuiz/1", "_blank");
  };

  const closeAlert = () => {
    setState((prevState) => ({
      ...prevState,
      alertState: false,
    }));
  };

  const transposeAssessmentData = (listData) => {
    const hasParent = listData.some((ld) => ld.parentAssessment === true);

    const returnList = listData.map((item) => {
      let statusText = item.currentStatus;
      let statusColor = getAssessmentStatusColor(item.assessmentStatus);
      const currentDay = new Date().getDate();
      const dueDateInt = parseInt(item.dueDate);

      if (dueDateInt > 0 && dueDateInt < currentDay) {
        switch (item.assessmentStatus) {
          case 5:
            statusText = "Completed (past due date)";
            statusColor = getAssessmentStatusColor(5);
            break;
          case 6:
            statusText = "Unsuccessful (past due date)";
            statusColor = getAssessmentStatusColor(6);
            break;
          case 7:
            statusText = "Due date passed";
            statusColor = getAssessmentStatusColor(7);
            break;
        }
      } else if (
        hasParent &&
        !item.parentAssessment &&
        item.currentStatus === "Awaiting Submission"
      ) {
        statusText = "";
      }

      const listItem = {
        controlId: item.assessmentID,
        assessmentID: item.assessmentID,
        name: item.assessmentID,
        showDetail: false,
        headingText: `${item.assessmentCode} - ${item.assessmentName} duedate: ${item.dDate}`,
        assessmentStatus: item.assessmentStatus,
        currentStatus: statusText,
        linkTarget: "",
        buttonIcon: UPLOADICON,
        buttonText: item.assessmentStatus === 4 ? "Resubmit" : "Submit",
        iconDocs: [
          {
            id: "assessment",
            showIconDocs: item?.assessmentDocuments?.length > 0,
            isclick: false,
          },
          {
            id: "submitted",
            showIconDocs: item.submissionDate,
            isclick: false,
          },
        ],
        hideButton:
          window?.assessmentsConfig &&
          !window?.assessmentsConfig?.submitAssessments,
        buttonFunction: null,
        itemList: [
          { itemText: item.description },
          {
            itemText: parser(
              `<span style="font-size: 0.875rem; font-weight: bold; color:${statusColor};">${statusText}</span>`
            ),
          },
          {
            itemText:
              item.submissionDate && item?.submissionDate !== ""
                ? `Submitted on: ${item?.submissionDate}`
                : "",
          },
          {
            itemText:
              item.currentStatus === "Completed Successfully" &&
              item.assessmentNotes !== ""
                ? `Notes: ${item.assessmentNotes}`
                : "",
          },
        ],
        assessmentDocuments: transposeAssessmentDocuments(item.assessmentDocuments),
        rawData: item,
      };

      if (
        (item.assessmentStatus === 1 || item.assessmentStatus === 4) &&
        (item.parentAssessment === true || !hasParent)
      ) {
        listItem.buttonFunction = item.quizId
          ? () => answerQuiz(item)
          : () => uploadDocument(item);
      }

      return listItem;
    });

    setState((pre) => ({ ...pre, listItems: returnList }));
  };

  if (pageLoading) {
    return <AssessmentsSkeleton />;
  }

  if (!state?.listItems?.length) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      {/* Alert */}
      {/* {state?.alertMessage && (
        <Alert
          alertStatus={state.alertState}
          closeFunction={closeAlert}
          alertMessage={state.alertMessage}
          alertIcon={state.alertIcon}
          iconColor={state.iconColor}
        />
      )} */}

      {/* Assessment Cards */}
      <div className="space-y-4">
        {state?.listItems?.map((assessment) => (
          <AssessmentCard
            key={assessment.controlId}
            assessment={assessment}
            programId={programId}
            onUpload={() => uploadDocument(assessment.rawData)}
          />
        ))}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={state.modalState}
        onClose={closeModal}
        onSubmit={submitAssessment}
        loading={loading}
        tempFiles={tempFiles}
        setTempFiles={setTempFiles}
        fields={state.fields}
        setFields={(fields) => setState((prev) => ({ ...prev, fields }))}
        error={formError}
        setFormError={setFormError}
      />
    </div>
  );
};

export default Assessments;